import * as React from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export interface PageHeaderProps {
  /** Page title */
  title: string;
  /** Optional subtitle/description */
  description?: string;
  /** Optional icon component */
  icon?: LucideIcon;
  /** Optional small label above title (e.g., "WELCOME BACK") */
  eyebrow?: string;
  /** Use gradient background (for hub/landing pages) */
  gradient?: boolean;
  /** Slim single-row header for dense list pages: icon + title, children at
   *  the right. Drops the eyebrow and shows the description as a tooltip
   *  (and to screen readers). Opt-in; every other caller is unchanged. */
  compact?: boolean;
  /** Additional className */
  className?: string;
  /** Children rendered after description (e.g., action buttons) */
  children?: React.ReactNode;
}

/**
 * Consistent page header for portal pages.
 *
 * Two variants:
 * - **Standard** (gradient=false): Simple header with title + description
 * - **Hub** (gradient=true): Gradient background with eyebrow text, for landing pages
 *
 * @example Standard header
 * ```tsx
 * <PageHeader
 *   title="CTA Presets"
 *   description="Manage call-to-action text presets"
 *   icon={MousePointerClick}
 * />
 * ```
 *
 * @example Hub header with gradient
 * ```tsx
 * <PageHeader
 *   title="Marketing Admin"
 *   description="Manage partners, templates, and content configuration"
 *   eyebrow="Administration"
 *   gradient
 * />
 * ```
 */
export function PageHeader({
  title,
  description,
  icon: Icon,
  eyebrow,
  gradient = false,
  compact = false,
  className,
  children,
}: PageHeaderProps) {
  if (compact) {
    return (
      <div className={cn("flex flex-wrap items-center justify-between gap-2", className)}>
        <h1 className="text-xl font-bold text-slate-900">
          {Icon && <Icon className="mr-2 inline-block h-5 w-5 align-text-bottom" />}
          {title}
          {description && <span className="sr-only">. {description}</span>}
        </h1>
        {children}
      </div>
    );
  }

  if (gradient) {
    return (
      <section
        className={cn(
          "rounded-2xl md:rounded-3xl border border-slate-100 bg-gradient-to-br from-primary/40 to-white p-4 md:p-8 shadow-sm",
          className
        )}
      >
        {eyebrow && (
          <p className="text-sm font-semibold uppercase tracking-[0.3em] text-primary">
            {eyebrow}
          </p>
        )}
        <h1 className={cn("text-2xl md:text-4xl font-bold text-slate-900", eyebrow && "mt-3")}>
          {Icon && <Icon className="mr-3 inline-block h-7 w-7 md:h-9 md:w-9 align-text-bottom" />}
          {title}
        </h1>
        {description && (
          <p className="mt-2 md:mt-4 max-w-2xl text-base md:text-lg text-slate-600">{description}</p>
        )}
        {children}
      </section>
    );
  }

  return (
    <div className={cn("flex flex-col gap-3 md:flex-row md:items-start md:justify-between", className)}>
      <div className="min-w-0">
        <h1 className="text-2xl md:text-4xl font-bold text-slate-900">
          {Icon && <Icon className="mr-2 md:mr-3 inline-block h-7 w-7 md:h-9 md:w-9 align-text-bottom" />}
          {title}
        </h1>
        {description && (
          <p className="mt-1 md:mt-2 text-base md:text-lg text-slate-600">{description}</p>
        )}
      </div>
      {children}
    </div>
  );
}
